import CourseSyllabus from "../common/CourseSyllabus/CourseSyllabus";

const mlSyllabus = {
    basic: [
        {
            id: "ml-basic-1",
            title: "Introduction to Machine Learning",
            content: [
                "What is Machine Learning?",
                "Machine Learning vs Artificial Intelligence",
                "Machine Learning vs Data Science",
                "Applications of Machine Learning",
                "Types of Machine Learning",
                "Machine Learning workflow",
            ],
        },

        {
            id: "ml-basic-2",
            title: "Fundamental ML Concepts",
            content: [
                "Supervised Learning",
                "Unsupervised Learning",
                "Semi-Supervised Learning",
                "Reinforcement Learning",
                "Training and testing data",
                "Features and labels",
                "Models and algorithms",
                "Machine Learning lifecycle",
            ],
        },

        {
            id: "ml-basic-3",
            title: "Mathematics for ML (Essentials)",
            content: [
                "Mathematical foundations for Machine Learning",
                "Variables and mathematical expressions",
                "Linear equations",
                "Vectors and matrices",
                "Matrix operations",
                "Probability fundamentals",
                "Mean, median, and mode",
                "Variance and standard deviation",
                "Basic statistics for Machine Learning",
            ],
        },

        {
            id: "ml-basic-4",
            title: "Understanding Datasets",
            content: [
                "What is a dataset?",
                "Features and target variables",
                "Structured and unstructured data",
                "Numerical and categorical data",
                "Training datasets",
                "Validation datasets",
                "Testing datasets",
                "Exploratory Data Analysis",
                "Identifying patterns and relationships",
            ],
        },

        {
            id: "ml-basic-5",
            title: "Data Preparation",
            content: [
                "Importance of data preparation",
                "Data cleaning",
                "Handling missing values",
                "Removing duplicate records",
                "Handling outliers",
                "Encoding categorical variables",
                "Feature scaling",
                "Normalization",
                "Standardization",
                "Preparing data for ML models",
            ],
        },
    ],

    intermediate: [
        {
            id: "ml-intermediate-1",
            title: "Supervised Learning - Regression",
            content: [
                "Introduction to Regression",
                "Regression workflow",
                "Simple Linear Regression",
                "Multiple Linear Regression",
                "Polynomial Regression",
                "Regression with Scikit-learn",
                "Training regression models",
                "Making predictions",
                "Regression evaluation metrics",
            ],
        },

        {
            id: "ml-intermediate-2",
            title: "Supervised Learning - Classification",
            content: [
                "Introduction to Classification",
                "Binary Classification",
                "Multi-Class Classification",
                "Logistic Regression",
                "K-Nearest Neighbors",
                "Decision Trees",
                "Support Vector Machines",
                "Classification with Scikit-learn",
                "Making classification predictions",
            ],
        },

        {
            id: "ml-intermediate-3",
            title: "Unsupervised Learning",
            content: [
                "Introduction to Unsupervised Learning",
                "Clustering concepts",
                "K-Means Clustering",
                "Hierarchical Clustering",
                "DBSCAN",
                "Dimensionality Reduction",
                "Principal Component Analysis",
                "Finding patterns in unlabeled data",
                "Practical clustering applications",
            ],
        },

        {
            id: "ml-intermediate-4",
            title: "Feature Engineering",
            content: [
                "What is Feature Engineering?",
                "Feature selection",
                "Feature extraction",
                "Creating new features",
                "Handling categorical features",
                "Encoding techniques",
                "Feature scaling",
                "Feature transformation",
                "Reducing irrelevant features",
                "Feature engineering best practices",
            ],
        },

        {
            id: "ml-intermediate-5",
            title: "Model Evaluation & Validation",
            content: [
                "Understanding model evaluation",
                "Training, validation, and test datasets",
                "Cross-validation",
                "Accuracy",
                "Precision",
                "Recall",
                "F1-score",
                "Confusion matrix",
                "Mean Absolute Error",
                "Mean Squared Error",
                "Root Mean Squared Error",
                "R² Score",
                "Overfitting and underfitting",
            ],
        },

        {
            id: "ml-intermediate-6",
            title: "Ensemble Learning",
            content: [
                "Introduction to Ensemble Learning",
                "Why use ensemble models?",
                "Bagging",
                "Boosting",
                "Random Forest",
                "Gradient Boosting",
                "AdaBoost",
                "XGBoost",
                "Voting and stacking",
                "Comparing ensemble models",
            ],
        },
    ],

    advanced: [
        {
            id: "ml-advanced-1",
            title: "Model Deployment",
            content: [
                "Introduction to ML model deployment",
                "Saving trained ML models",
                "Loading trained models",
                "Building prediction APIs",
                "Deploying models with Python",
                "Flask and FastAPI basics",
                "Model serving",
                "REST API integration",
                "Deploying ML applications",
            ],
        },

        {
            id: "ml-advanced-2",
            title: "Natural Language Processing (NLP)",
            content: [
                "Introduction to NLP",
                "Understanding text data",
                "Text preprocessing",
                "Tokenization",
                "Stop words",
                "Stemming and Lemmatization",
                "Bag of Words",
                "TF-IDF",
                "Text classification",
                "Sentiment analysis",
                "Introduction to word embeddings",
            ],
        },

        {
            id: "ml-advanced-3",
            title: "Time Series Forecasting",
            content: [
                "Introduction to Time Series",
                "Time series components",
                "Trend and seasonality",
                "Stationarity",
                "Time series data preparation",
                "Moving averages",
                "ARIMA fundamentals",
                "Forecasting models",
                "Model evaluation for forecasting",
                "Practical forecasting applications",
            ],
        },

        {
            id: "ml-advanced-4",
            title: "Recommendation Systems",
            content: [
                "Introduction to Recommendation Systems",
                "Types of recommendation systems",
                "Content-Based Filtering",
                "Collaborative Filtering",
                "User-item interaction",
                "Similarity measures",
                "Building recommendation models",
                "Recommendation system evaluation",
                "Practical recommendation applications",
            ],
        },

        {
            id: "ml-advanced-5",
            title: "Deep Learning for ML",
            content: [
                "Introduction to Deep Learning",
                "Neural network fundamentals",
                "Artificial neurons",
                "Layers and network architecture",
                "Activation functions",
                "Forward propagation",
                "Backpropagation",
                "Loss functions",
                "Optimizers",
                "Training neural networks",
            ],
        },

        {
            id: "ml-advanced-6",
            title: "Advanced Deep Learning Techniques",
            content: [
                "Convolutional Neural Networks",
                "CNN architecture",
                "Image classification",
                "Recurrent Neural Networks",
                "Sequence data",
                "LSTM networks",
                "Transfer Learning",
                "Dropout and regularization",
                "Batch normalization",
                "Advanced neural network architectures",
            ],
        },

        {
            id: "ml-advanced-7",
            title: "ML in Production (MLOps Overview)",
            content: [
                "Introduction to MLOps",
                "Machine Learning lifecycle in production",
                "Model versioning",
                "Data versioning",
                "Experiment tracking",
                "Model monitoring",
                "Model performance tracking",
                "CI/CD for Machine Learning",
                "ML pipelines",
                "Model retraining",
                "Introduction to Docker for ML",
                "Production ML best practices",
            ],
        },
    ],
};

function MLSyllabus() {
    return (
        <CourseSyllabus
            courseName="Machine Learning"
            syllabus={mlSyllabus}
        />
    );
}

export default MLSyllabus;